import React from "react";
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

function FilterSelect({ label, value, options, onChange, width }) {
  return (
    <Box
      sx={{
        width: width || { xs: 102, sm: 102 },
        flex: "0 0 auto",
        borderRadius: 1.6,
        px: 1,
        py: 0.55,
        backgroundColor: "#0f485e",
        border: "1px solid rgba(120, 197, 219, 0.3)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "#a9d7e5",
          display: "block",
          mb: 0.2,
          fontSize: "0.66rem",
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
      <FormControl fullWidth size="small" variant="standard">
        <Select
          disableUnderline
          value={value}
          onChange={onChange}
          sx={{
            color: "#e4f6fc",
            fontWeight: 600,
            fontSize: "0.78rem",
            minHeight: 20,
            "& .MuiSelect-select": { py: 0 },
            "& .MuiSelect-icon": { color: "#c7edf8" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#0f485e",
                color: "#e4f6fc",
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default function PerformanceFiltersCard({
  t,
  latestSelectableDate,
  timePreset,
  setTimePreset,
  multiMonthSpan,
  setMultiMonthSpan,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  filterOptions,
  withAllOption,
  building,
  setBuilding,
  floor,
  setFloor,
  department,
  setDepartment,
  room,
  setRoom,
  robot,
  setRobot,
  route,
  setRoute,
  clearFilters,
  hasActiveFilters,
  showViewToggle,
  viewMode,
  setViewMode,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.1,
        borderRadius: 3,
        backgroundColor: "#0d3d4f",
        border: "1px solid rgba(135, 206, 228, 0.33)",
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={0.7}
        flexWrap="wrap"
        alignItems="center"
        useFlexGap
      >
        <FilterSelect
          label={t("filters.timeRange")}
          value={timePreset}
          onChange={(event) => setTimePreset(event.target.value)}
          width={{ xs: 132, sm: 140 }}
          options={[
            { value: "week", label: t("filters.week") },
            { value: "month", label: t("filters.month") },
            { value: "multiMonth", label: t("filters.multiMonth") },
            { value: "year", label: t("filters.year") },
            { value: "custom", label: t("filters.custom") },
          ]}
        />

        {timePreset === "multiMonth" && (
          <FilterSelect
            label={t("filters.monthSpan")}
            value={String(multiMonthSpan)}
            onChange={(event) =>
              setMultiMonthSpan(Number(event.target.value) || 3)
            }
            width={{ xs: 132, sm: 132 }}
            options={[
              { value: "2", label: "2" },
              { value: "3", label: "3" },
              { value: "6", label: "6" },
              { value: "12", label: "12" },
            ]}
          />
        )}

        <Box
          sx={{
            width: { xs: 132, sm: 150 },
            flex: "0 0 auto",
            borderRadius: 1.6,
            px: 1,
            py: 0.55,
            backgroundColor: "#0f485e",
            border: "1px solid rgba(120, 197, 219, 0.3)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#a9d7e5",
              display: "block",
              mb: 0.2,
              fontSize: "0.66rem",
              lineHeight: 1.1,
            }}
          >
            {t("filters.from")}
          </Typography>
          <TextField
            type="date"
            variant="standard"
            fullWidth
            value={fromDate}
            inputProps={{ max: latestSelectableDate }}
            onChange={(event) => {
              setFromDate(event.target.value);
              setTimePreset("custom");
            }}
            InputProps={{
              disableUnderline: true,
              sx: {
                color: "#e4f6fc",
                fontWeight: 600,
                fontSize: "0.78rem",
                "& input": { py: 0 },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            width: { xs: 132, sm: 150 },
            flex: "0 0 auto",
            borderRadius: 1.6,
            px: 1,
            py: 0.55,
            backgroundColor: "#0f485e",
            border: "1px solid rgba(120, 197, 219, 0.3)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#a9d7e5",
              display: "block",
              mb: 0.2,
              fontSize: "0.66rem",
              lineHeight: 1.1,
            }}
          >
            {t("filters.to")}
          </Typography>
          <TextField
            type="date"
            variant="standard"
            fullWidth
            value={toDate}
            inputProps={{ max: latestSelectableDate }}
            onChange={(event) => {
              setToDate(event.target.value);
              setTimePreset("custom");
            }}
            InputProps={{
              disableUnderline: true,
              sx: {
                color: "#e4f6fc",
                fontWeight: 600,
                fontSize: "0.78rem",
                "& input": { py: 0 },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            alignSelf: "stretch",
            width: 2,
            marginLeft: 2,
            marginRight: 2,
            marginTop: 0.5,
            marginBottom: 0.5,
            backgroundColor: "rgba(145, 210, 230, 0.2)",
          }}
        />

        <FilterSelect
          label={t("filters.area")}
          value={building}
          onChange={(event) => setBuilding(event.target.value)}
          options={withAllOption(filterOptions.buildings)}
        />
        <FilterSelect
          label={t("filters.floor")}
          value={floor}
          onChange={(event) => setFloor(event.target.value)}
          options={withAllOption(filterOptions.floors)}
        />
        <FilterSelect
          label={t("filters.building")}
          value={department}
          onChange={(event) => setDepartment(event.target.value)}
          options={withAllOption(filterOptions.departments)}
        />
        <FilterSelect
          label={t("filters.room")}
          value={room}
          onChange={(event) => setRoom(event.target.value)}
          options={withAllOption(filterOptions.rooms)}
        />
        <FilterSelect
          label={t("filters.robot")}
          value={robot}
          onChange={(event) => setRobot(event.target.value)}
          options={withAllOption(filterOptions.robots)}
        />
        <FilterSelect
          label={t("filters.route")}
          value={route}
          onChange={(event) => setRoute(event.target.value)}
          options={withAllOption(filterOptions.routes)}
        />

        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.72rem",
              borderColor: "rgba(145, 210, 230, 0.45)",
              color: "#bfe8f5",
              px: 1,
              py: 0.2,
              minWidth: 58,
              alignSelf: "center",
              "&.Mui-disabled": {
                borderColor: "rgba(145, 210, 230, 0.2)",
                color: "rgba(191, 232, 245, 0.45)",
              },
            }}
          >
            {t("filters.clear")}
          </Button>
          <Box
            sx={{
              display: showViewToggle ? "flex" : "none",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#a9d7e5", fontWeight: 700 }}
            >
              {t("view")}
            </Typography>
            <ButtonGroup
              variant="outlined"
              size="small"
              sx={{
                "& .MuiButton-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  borderColor: "rgba(145, 210, 230, 0.45)",
                  color: "#bfe8f5",
                  px: 1,
                  py: 0.2,
                  minWidth: 58,
                },
              }}
            >
              <Button
                onClick={() => setViewMode("graph")}
                sx={{
                  backgroundColor:
                    viewMode === "graph"
                      ? "rgba(86, 194, 227, 0.24)"
                      : "transparent",
                }}
              >
                {t("viewGraph")}
              </Button>
              <Button
                onClick={() => setViewMode("list")}
                sx={{
                  backgroundColor:
                    viewMode === "list"
                      ? "rgba(86, 194, 227, 0.24)"
                      : "transparent",
                }}
              >
                {t("viewList")}
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}
